import { Box } from '@mui/material';
import { compact, includes } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SetRequired } from 'type-fest';

import { Button } from '@/components/form/baseInputs/Button';
import { DropdownButton } from '@/components/form/baseInputs/DropdownButton/DropdownButton';
import { LinkExternal01Icon } from '@/components/icons/LinkExternal01Icon';
import { SidePanel } from '@/components/modals/SidePanel';
import { DispositiveProvisionSuggestionsProvider } from '@/modules/aiDP/context/DispositiveProvisionSuggestions.provider';
import { EntitySuggestionsProvider } from '@/modules/aiSuggestions/context/EntitySuggestions.provider';
import {
  EntityDetails,
  EntityDetailTab,
  EntityTabComponentMap,
} from '@/modules/entities/components/EntityDetails';
import { EntityDetailsAwareRoute } from '@/modules/entities/contexts/entityDetails/EntityDetailsAwareRoute';
import { useEntityPageDefinition } from '@/modules/entities/details/EntityDetailOverviewPage';
import {
  BusinessEntityDetailsTabs,
  NonTrustCharitableEntityDetailsTabs,
  PersonalAccountDetailsTabs,
  TrustDetailsTabs,
} from '@/modules/entities/details/entityDetails.types';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { getSectionForEntityDetailsActiveTab } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.utils';
import { EditEntitySplitScreenModal } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenModal';
import { EditEntitySplitScreenOpenModalButton } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenOpenModalButton';
import { useEditEntityTrowser } from '@/modules/entities/EditEntitySplitScreen/hooks/useEditEntityTrowser';
import { EntityFormEditButton } from '@/modules/entities/EntityForm/EntityFormEditButton';
import { EntitySubformNamespaceType } from '@/modules/entities/EntitySubforms/entitySubformConfigs';
import { getStageAwareLinkForEntity } from '@/modules/entities/utils/entitiesNavigationUtils';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';

import { SummaryPanelLoader } from '../components/SummaryPanelLoader';

export interface EntitySummaryPanelProps {
  entityId: string;
  householdId: string;
  onClose: () => void;
  isOnHypotheticalWaterfall: boolean;
  isHypotheticalEntity: boolean;
  isSummaryView?: boolean;
  isOnSourceWaterfall?: boolean;
  initialTabOverride?: string;
  showGoToEntityButton?: boolean;
  isOnEntityPage?: boolean;
  isOnSingleEntityDiagram?: boolean;
  backLabel?: string;
  onBack?: () => void;
}

const AssetsTab = 'Assets';
const TransfersTab = 'Hypothetical transfers';
const CashFlowsTab = 'CashFlows';

export const sidePanelSummaryTabs = [
  TrustDetailsTabs.SUMMARY,
  NonTrustCharitableEntityDetailsTabs.SUMMARY,
  PersonalAccountDetailsTabs.SUMMARY,
  BusinessEntityDetailsTabs.SUMMARY,
];

// Show the footer for tabs associated with
// the entity details input form
const footerVisibleTabs = [
  ...sidePanelSummaryTabs,
  TrustDetailsTabs.BENEFICIARIES,
  TrustDetailsTabs.POLICIES,
  TrustDetailsTabs.TRUSTEES,
  NonTrustCharitableEntityDetailsTabs.BENEFICIARIES,
  PersonalAccountDetailsTabs.BENEFICIARIES,
  BusinessEntityDetailsTabs.OWNERSHIP,
  BusinessEntityDetailsTabs.KEY_PEOPLE,
];

type CurrentView =
  | EntityDetailTab
  | { display: string; value: typeof AssetsTab }
  | { display: string; value: typeof TransfersTab }
  | { display: string; value: typeof CashFlowsTab };

const CURRENT_VIEW_TYPE_TO_ENTITY_SUBFORM_MAP: Partial<
  Record<CurrentView['value'], EntitySubformNamespaceType>
> = {
  trustees: 'trustDetailsSubform',
  beneficiaries: 'beneficiariesSubform',
  summary: 'basicInformationSubform',
  policies: 'insurancePolicyDetailsSubform',
};

function EntitySummaryPanelInner({
  entityId,
  onClose,
  isOnHypotheticalWaterfall = false,
  isHypotheticalEntity = false,
  isSummaryView = false,
  isOnSourceWaterfall = false,
  initialTabOverride,
  showGoToEntityButton = true,
  isOnEntityPage,
  isOnSingleEntityDiagram = false,
  backLabel,
  onBack,
}: EntitySummaryPanelProps) {
  const { entityId: routeEntityId } = useParams();
  // When the summary panel is for a different entity than the route (e.g. a
  // trust owner on a business entity's diagram), the panel must render its own
  // EditEntitySplitScreenModal. When it's the same entity, the entity page's
  // modal handles it via URL params.
  const isEditingNonRouteEntity =
    isOnSingleEntityDiagram && entityId !== routeEntityId;
  const [currentView, setCurrentView] = useState<CurrentView>();
  const {
    entity,
    entityPageDefinition: {
      summary: summaryTabs,
      AssetsCard,
      TransfersCard,
      CashFlowsCard,
    },
    loading,
  } = useEntityPageDefinition(entityId, { isPanelView: true });

  const summaryTabsWithAssetsAndTransfers: CurrentView[] | undefined =
    useMemo(() => {
      if (loading) {
        return;
      }

      return [
        ...summaryTabs,
        {
          display: 'Current holdings',
          value: AssetsTab,
          props: {},
        },
        {
          display: TransfersTab,
          value: TransfersTab,
          props: {},
        },
        {
          display: 'Cash flows',
          value: CashFlowsTab,
          props: {},
        },
      ];
    }, [loading, summaryTabs]);

  const items = useMemo(() => {
    if (!summaryTabsWithAssetsAndTransfers) {
      return [];
    }

    return compact(
      summaryTabsWithAssetsAndTransfers.flatMap(({ display, value }) => {
        // Hide transfers tab when not on a hypothetical waterfall or hypothetical waterfall
        // summary view
        if (!isOnHypotheticalWaterfall && value === TransfersTab) {
          return [];
        }

        // Hide assets tab for draft entities since
        // we don't want to confuse users. Draft entity
        // assets are not accounted for when calculating hypothetical
        // waterfall values, so this asset value could differ from
        // the value shown in the waterfall.
        if (isHypotheticalEntity && value === AssetsTab) {
          return [];
        }

        return {
          name: display,
          clickHandler: () => setCurrentView({ display, value }),
        };
      })
    );
  }, [
    isHypotheticalEntity,
    isOnHypotheticalWaterfall,
    summaryTabsWithAssetsAndTransfers,
  ]);

  const handleSetCurrentView = useCallback(
    (tabs: CurrentView[]) => {
      // Check for initialTab first from props
      if (initialTabOverride) {
        const tabWithInitialValue = tabs.find(
          (tab) => tab.value === initialTabOverride
        );
        if (tabWithInitialValue) {
          setCurrentView(tabWithInitialValue);
          return;
        }
      }

      // Otherwise use the existing logic for tab selection
      if (isOnHypotheticalWaterfall) {
        // Set the initial tab to the transfers tab
        // when on a hypothetical waterfall
        setCurrentView(tabs.find((t) => t.value === TransfersTab) ?? tabs[0]);
      } else if (isOnSourceWaterfall || isOnSingleEntityDiagram) {
        // Set the initial tab to the dispositions tab
        // when on a source waterfall or single entity diagram
        setCurrentView(
          tabs.find(
            (t) =>
              t.value === TrustDetailsTabs.DISPOSITIONS ||
              t.value === PersonalAccountDetailsTabs.DISPOSITIONS ||
              t.value === NonTrustCharitableEntityDetailsTabs.DISPOSITIONS
          ) ?? tabs[0]
        );
      } else {
        setCurrentView(tabs[0]);
      }
    },
    [
      isOnHypotheticalWaterfall,
      isOnSourceWaterfall,
      isOnSingleEntityDiagram,
      initialTabOverride,
    ]
  );

  useEffect(
    function handleSetCurrentViewOnLoad() {
      // Once our available tabs are loaded, set the current view
      // using the tab reset handler.
      if (summaryTabsWithAssetsAndTransfers && !currentView) {
        handleSetCurrentView(summaryTabsWithAssetsAndTransfers);
      }
    },
    [
      currentView,
      isSummaryView,
      handleSetCurrentView,
      summaryTabsWithAssetsAndTransfers,
    ]
  );

  useEffect(
    function handleResetCurrentViewOnTabsChange() {
      // If the current view is not in the available tabs
      // call the reset handler to set the default current view.
      if (currentView && summaryTabsWithAssetsAndTransfers) {
        const activeTab =
          summaryTabsWithAssetsAndTransfers.find(
            (tab) => tab.value === currentView.value
          ) ?? null;

        if (!activeTab) {
          handleSetCurrentView(summaryTabsWithAssetsAndTransfers);
        }
      }
    },
    [currentView, handleSetCurrentView, summaryTabsWithAssetsAndTransfers]
  );

  const initialFocusedSubformType = currentView?.value
    ? CURRENT_VIEW_TYPE_TO_ENTITY_SUBFORM_MAP[currentView.value]
    : undefined;

  const entityKind = entity?.kind;
  const initialSectionOptions = useMemo(() => {
    if (!currentView?.value || !entityKind) {
      return undefined;
    }

    function isEntityDetailTab(view: CurrentView): view is EntityDetailTab {
      return Object.keys(EntityTabComponentMap).includes(view.value);
    }

    if (isEntityDetailTab(currentView)) {
      return getSectionForEntityDetailsActiveTab(
        currentView.value,
        getEntityTypeFromEntityKind(entityKind)
      );
    }

    return undefined;
  }, [currentView, entityKind]);

  const {
    isOpenForEntity,
    section: editTrowserSection,
    close: closeEditEntityModal,
  } = useEditEntityTrowser();
  const isEditEntityModalOpen = isOpenForEntity(entityId);
  // The trowser carries only URL state; derive the entity type from the loaded
  // entity (this panel only edits the entity it's showing).
  const editEntityType = entity?.kind
    ? getEntityTypeFromEntityKind(entity.kind)
    : undefined;

  return (
    <>
      <SidePanel.Panel data-testid="EntitySummaryPanel">
        <SidePanel.Header
          title={entity?.subtype?.displayName}
          backLabel={backLabel}
          onBack={onBack}
          onClose={onClose}
        >
          <DropdownButton
            showArrow
            name="Dropdown-selectView"
            variant="secondary"
            size="sm"
            buttonContent={currentView?.display || ''}
            items={items}
            contentBoxSx={{
              width: '100%',
              justifyContent: 'space-between',
            }}
          />
        </SidePanel.Header>
        <SidePanel.Content centerEmptyState>
          <SidePanel.Section>
            {loading && <SummaryPanelLoader />}
            <Box display={loading ? 'none' : 'block'}>
              {currentView &&
                currentView.value !== AssetsTab &&
                currentView.value !== TransfersTab &&
                currentView.value !== CashFlowsTab && (
                  <EntityDetails
                    tabs={summaryTabs}
                    activeEntityView={currentView.value}
                  />
                )}
              {currentView?.value === AssetsTab && AssetsCard}
              {currentView?.value === TransfersTab && TransfersCard}
              {currentView?.value === CashFlowsTab && CashFlowsCard}
            </Box>
          </SidePanel.Section>
        </SidePanel.Content>
        {includes(footerVisibleTabs, currentView?.value) && (
          <SidePanel.Footer>
            {showGoToEntityButton && (
              <Button
                size="sm"
                variant="secondary"
                target="_blank"
                href={
                  entity
                    ? getStageAwareLinkForEntity(
                        // TODO: for some reason our graphql codegen is not marking __typename as defined when it should be
                        entity as Omit<
                          NonNullable<typeof entity>,
                          'subtype'
                        > & {
                          subtype: SetRequired<
                            NonNullable<typeof entity>['subtype'],
                            '__typename'
                          >;
                        },
                        isHypotheticalEntity
                      )
                    : ''
                }
                disabled={loading}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  Open in new tab
                  <LinkExternal01Icon size={18} />
                </Box>
              </Button>
            )}
            {initialSectionOptions ? (
              <EditEntitySplitScreenOpenModalButton
                initialSection={
                  initialSectionOptions.length === 1
                    ? initialSectionOptions[0]
                    : undefined
                }
                initialSectionOptions={
                  initialSectionOptions.length === 1
                    ? undefined
                    : initialSectionOptions
                }
              />
            ) : (
              <EntityFormEditButton
                householdId={entity?.household.id ?? ''}
                entityId={entity?.id ?? ''}
                entityType={
                  entity?.kind && getEntityTypeFromEntityKind(entity.kind)
                }
                disabled={loading}
                isHypotheticalEntity={isHypotheticalEntity}
                initialFocusedSubformType={initialFocusedSubformType}
              />
            )}
          </SidePanel.Footer>
        )}
      </SidePanel.Panel>
      {isEditEntityModalOpen &&
        editEntityType &&
        (!isOnEntityPage || isEditingNonRouteEntity) && (
          <EditEntitySplitScreenModal
            entityType={editEntityType}
            initialSection={
              editTrowserSection ?? EditEntitySection.BASIC_INFORMATION
            }
            isOpen
            onClose={closeEditEntityModal}
          />
        )}
    </>
  );
}

export const EntitySummaryPanel = (props: EntitySummaryPanelProps) => {
  const { entityId } = props;

  return (
    <EntityDetailsAwareRoute entityId={entityId}>
      <EntitySuggestionsProvider>
        <DispositiveProvisionSuggestionsProvider targetId={entityId}>
          <EntitySummaryPanelInner {...props} />
        </DispositiveProvisionSuggestionsProvider>
      </EntitySuggestionsProvider>
    </EntityDetailsAwareRoute>
  );
};
